import { useEffect, useRef, useState } from 'react';

import { isTouchSupported } from '../utils';

interface UseTouchHandlersParams {
    onTouchStart?: () => void;
    onTouchEnd?: () => void;
    onTouchEnter?: () => void;
    onTouchLeave?: () => void;
    elementRef: React.RefObject<HTMLElement>;
    active?: boolean;
}

export const useTouchHandlers = (
    { onTouchEnter, onTouchLeave, onTouchEnd, onTouchStart, elementRef, active }: UseTouchHandlersParams,
    refreshCallbacksParams: unknown[] = [],
) => {
    const [isTouchInside, setIsTouchInside] = useState(false);
    // Реф нужен для корректной обработки touchevents при смене флага, т.к. при замене обработчика
    // продолжает использоваться старый, до события touchend
    const isTouchInsideRef = useRef(false);

    const isTouchDevice = isTouchSupported();
    const skipListeners = !active || !isTouchDevice;

    useEffect(() => {
        if (skipListeners) {
            return;
        }

        const touchStartListener = (e: TouchEvent) => {
            const { clientX: x, clientY: y } = e.touches[0];

            const activeElements = document.elementsFromPoint(x, y);
            const isCurrentElementActive = activeElements.includes(elementRef.current!);

            if (isCurrentElementActive) {
                e.preventDefault();
                onTouchStart && onTouchStart();
                isTouchInsideRef.current = true;
                setIsTouchInside(true);
            }
        };

        document.addEventListener('touchstart', touchStartListener, { passive: false });

        return () => document.removeEventListener('touchstart', touchStartListener);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, ...refreshCallbacksParams]);

    useEffect(() => {
        if (skipListeners) {
            return;
        }

        const touchEndListener = () => {
            onTouchEnd && onTouchEnd();
            isTouchInsideRef.current = false;
            setIsTouchInside(false);
        };

        document.addEventListener('touchend', touchEndListener, { passive: false });

        return () => document.removeEventListener('touchend', touchEndListener);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, ...refreshCallbacksParams]);

    useEffect(() => {
        if (skipListeners) {
            return;
        }

        const touchMoveListener = (e: TouchEvent) => {
            e.preventDefault();

            const { clientX: x, clientY: y } = e.touches[0];

            const activeElements = document.elementsFromPoint(x, y);
            const isCurrentElementActive = activeElements.includes(elementRef.current!);

            if (!isTouchInsideRef.current && isCurrentElementActive) {
                onTouchEnter && onTouchEnter();
                isTouchInsideRef.current = true;
                setIsTouchInside(true);
                return;
            }
        };

        document.addEventListener('touchmove', touchMoveListener, { passive: false });

        return () => document.removeEventListener('touchmove', touchMoveListener);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, ...refreshCallbacksParams]);

    useEffect(() => {
        if (skipListeners) {
            return;
        }

        const touchMoveListener = (e: TouchEvent) => {
            e.preventDefault();

            const { clientX: x, clientY: y } = e.touches[0];

            const activeElements = document.elementsFromPoint(x, y);
            const isCurrentElementActive = activeElements.includes(elementRef.current!);

            if (isTouchInsideRef.current && !isCurrentElementActive) {
                onTouchLeave && onTouchLeave();
                isTouchInsideRef.current = false;
                setIsTouchInside(false);

                return;
            }
        };

        document.addEventListener('touchmove', touchMoveListener, { passive: false });

        return () => document.removeEventListener('touchmove', touchMoveListener);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, ...refreshCallbacksParams]);

    return { isTouchInside };
};
